import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formatta il nome del file della foto di una persona secondo le regole Gesvol:
 * - <cognome>_<nome>.webp
 * - Tutto in minuscolo
 * - Spazi convertiti in '_'
 * - Caratteri strani (apici, ecc.) rimossi
 * - Lettere accentate sostituite con corrispondenti semplici
 */
export function getPersonPhotoPath(cognome: string = '', nome: string = '', isGrid: boolean = false): string {
  const normalize = (str: string) => {
    return str
      .normalize('NFD') // Decompone i caratteri accentati (es: é -> e + ´)
      .replace(/[\u0300-\u036f]/g, '') // Rimuove i segni diacritici (gli accenti)
      .replace(/['"]/g, '') // Rimuove apici e doppi apici
      .replace(/\s+/g, '_') // Sostituisce spazi con underscore
      .toLowerCase();
  };

  const c = normalize(cognome.trim());
  const n = normalize(nome.trim());

  if (!c && !n) return '';
  
  if (isGrid) {
    return `images/persone_40x40/${c}_${n}_40x40.webp`;
  }
  return `images/persone/${c}_${n}.webp`;
}

/**
 * Converte una stringa in Title Case (Capfirst per ogni parola)
 */
export function toTitleCase(str: string = ''): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(/[\s']+/)
    .map(word => {
      if (word.length === 0) return '';
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' '); // Note: split by space or apostrophe, but joining with space might be lossy for apostrophes.
    // Better implementation for Italian names (handle d'Orazio etc.)
}

/**
 * Converte una stringa in Title Case per nomi italiani (gestisce spazi e apostrofi)
 */
export function formatName(str: string = ''): string {
  if (!str) return '';
  return str
    .toLowerCase()
    .replace(/(?:^|[\s'])\S/g, (match) => match.toUpperCase());
}

/**
 * Calcola l'età in anni compiuti a oggi partendo da una data di nascita.
 * Supporta formati 'DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD' o Date.
 */
export function calculateAge(birthDateVal: string | Date | null | undefined): number | null {
  if (!birthDateVal) return null;
  
  let birthDate: Date;
  if (birthDateVal instanceof Date) {
    birthDate = birthDateVal;
  } else if (typeof birthDateVal === 'string') {
    const trimmed = birthDateVal.trim();
    if (!trimmed) return null;
    
    // Supporto formato italiano DD/MM/YYYY o DD-MM-YYYY
    if (trimmed.includes('/') || (trimmed.includes('-') && trimmed.split('-')[0].length <= 2)) {
      const separator = trimmed.includes('/') ? '/' : '-';
      const parts = trimmed.split(separator);
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        birthDate = new Date(year, month, day);
      } else {
        birthDate = new Date(trimmed);
      }
    } else {
      birthDate = new Date(trimmed);
    }
  } else {
    return null;
  }

  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? age : null;
}

export type AgeOperator = '<' | '>' | '<>' | '<=' | '>=' | '!=' | '=';

export interface AgeFilter {
  op: AgeOperator;
  targetAge: number;
}

/**
 * Riconosce se un termine di ricerca è un filtro per età:
 * operatore: < > <> (oppure <=, >=, !=, =)
 * numero: 0..99
 * es: "> 65", "< 35", "<> 80", ">65", "<35", "<>80"
 */
export function parseAgeFilter(term: string): AgeFilter | null {
  if (!term) return null;
  const trimmed = term.trim();
  // RegEx che accetta <, >, <>, <=, >=, !=, = seguito da un numero da 0 a 99
  const match = trimmed.match(/^(<>|<=|>=|!=|<|>|=)\s*(\d{1,2})$/);
  if (!match) return null;
  const op = match[1] as AgeOperator;
  const targetAge = parseInt(match[2], 10);
  if (isNaN(targetAge) || targetAge < 0 || targetAge > 99) return null;
  return { op, targetAge };
}

/**
 * Valuta la condizione dell'operatore sull'età calcolata
 */
export function evaluateAgeCondition(age: number | null, op: AgeOperator, targetAge: number): boolean {
  if (age === null || age === undefined || isNaN(age)) return false;
  switch (op) {
    case '>':
      return age > targetAge;
    case '<':
      return age < targetAge;
    case '<>':
    case '!=':
      return age !== targetAge;
    case '>=':
      return age >= targetAge;
    case '<=':
      return age <= targetAge;
    case '=':
      return age === targetAge;
    default:
      return false;
  }
}

